import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDebounce } from "use-debounce";
import {
  GlobalSearchFilterTypes,
  GlobalSearchEntityTypes,
  GlobalSearchResult,
  GlobalSearchStates,
  FILTER_TO_ENTITY_TYPES
} from "../types";
import {
  useGlobalSearchMutation,
  prepareSearchRequest
} from "./useGlobalSearchAPI";

export const useGlobalSearch = (
  enabledEntityTypes?: GlobalSearchEntityTypes[],
  queryBy?: string
) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<GlobalSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState<GlobalSearchFilterTypes>(
    GlobalSearchFilterTypes.ALL
  );
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [hasKeyboardNavigation, setHasKeyboardNavigation] = useState(false);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const [debouncedQuery] = useDebounce(query, 300);

  const globalSearchMutation = useGlobalSearchMutation();
  const latestRequestId = useRef(0);

  useEffect(() => {
    const requestId = ++latestRequestId.current;

    const performSearch = async (
      searchQuery: string,
      filter: GlobalSearchFilterTypes
    ) => {
      if (!searchQuery.trim()) {
        setResults([]);
        setTotal(0);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const searchRequest = prepareSearchRequest(
          searchQuery,
          filter,
          1,
          20,
          "relevance",
          enabledEntityTypes,
          queryBy
        );
        const response = await globalSearchMutation.mutateAsync(searchRequest);
        if (requestId !== latestRequestId.current) return;
        setResults(response.results || []);
        setTotal(response.total || 0);
        setSelectedIndex(-1);
        setHasKeyboardNavigation(false);
      } catch (err) {
        if (requestId !== latestRequestId.current) return;
        console.error("Search error:", err);
        setError(t("globalSearch.errors.searchFailed"));
        setResults([]);
        setTotal(0);
      } finally {
        if (requestId === latestRequestId.current) {
          setIsLoading(false);
        }
      }
    };

    performSearch(debouncedQuery, activeFilter);
  }, [debouncedQuery, activeFilter, enabledEntityTypes, queryBy]);

  const clearSearch = useCallback(() => {
    setQuery("");
    setResults([]);
    setTotal(0);
    setSelectedIndex(-1);
    setHasKeyboardNavigation(false);
    setError(null);
    setIsLoading(false);
  }, []);

  const updateSelectedIndex = useCallback(
    (newIndex: number, fromKeyboard: boolean = false) => {
      if (results.length === 0) {
        setSelectedIndex(-1);
        return;
      }

      if (fromKeyboard) {
        setHasKeyboardNavigation(true);
      }

      const boundedIndex = Math.max(-1, Math.min(newIndex, results.length - 1));
      setSelectedIndex(boundedIndex);
    },
    [results.length]
  );

  const handleFilterChange = useCallback((filter: GlobalSearchFilterTypes) => {
    setActiveFilter(filter);
    setSelectedIndex(-1);
    setHasKeyboardNavigation(false);
  }, []);

  const filteredResults = useMemo(() => {
    if (activeFilter === GlobalSearchFilterTypes.ALL) {
      return results;
    }
    const mergedEntityTypes = FILTER_TO_ENTITY_TYPES[activeFilter];
    if (mergedEntityTypes !== undefined) {
      return results.filter((result) =>
        mergedEntityTypes.includes(result.entityType)
      );
    }
    return results.filter(
      (result) =>
        result.entityType ===
        (activeFilter as unknown as GlobalSearchEntityTypes)
    );
  }, [results, activeFilter]);

  const searchState = useMemo((): GlobalSearchStates => {
    if (!query.trim()) return GlobalSearchStates.IDLE;
    if (isLoading) return GlobalSearchStates.LOADING;
    if (error) return GlobalSearchStates.EMPTY; // treating error as empty for now
    if (results.length === 0) return GlobalSearchStates.EMPTY;
    return GlobalSearchStates.RESULTS;
  }, [query, isLoading, error, results.length]);

  const navigateUp = useCallback(() => {
    updateSelectedIndex(selectedIndex - 1, true);
  }, [selectedIndex, updateSelectedIndex]);

  const navigateDown = useCallback(() => {
    updateSelectedIndex(selectedIndex + 1, true);
  }, [selectedIndex, updateSelectedIndex]);

  const getSelectedResult = useCallback(() => {
    return results[selectedIndex] || null;
  }, [results, selectedIndex]);

  return {
    query,
    results: filteredResults,
    isLoading,
    activeFilter,
    selectedIndex,
    hasKeyboardNavigation,
    total,
    error,
    searchState,
    setQuery,
    setActiveFilter: handleFilterChange,
    setSelectedIndex: updateSelectedIndex,
    clearSearch,
    navigateUp,
    navigateDown,
    getSelectedResult,
    hasResults: results.length > 0,
    isEmpty: !isLoading && query.trim() !== "" && results.length === 0,
    isIdle: !query.trim()
  };
};
